import './BrandingLocation.scss';
import React from 'react';
import {Box, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import ArrButton, {
  ArrButtonVariant,
} from '../../../../common/arr-button/ArrButton';
import {Add as AddIcon} from '@mui/icons-material';
import {BrandingPositionKey} from '../../../../../api/types/production.type';

interface BrandingLocationProps {
  currentBrandingPosition: BrandingPositionKey;
  expandedPlusButtons: {[key: string]: boolean};
  onBrandingPositionChange: (position: BrandingPositionKey) => void;
  onPlusButtonClick: (key: string) => void;
  onReset: () => void;
  isResetEnabled: boolean;
  getBrandingPositionButtonVariant: (
    position: BrandingPositionKey
  ) => ArrButtonVariant;
  getBrandingPositionButtonClassName: (position: BrandingPositionKey) => string;
  getPositionDisplayText: (position: BrandingPositionKey) => string;
}

const BrandingLocation: React.FC<BrandingLocationProps> = ({
  currentBrandingPosition,
  expandedPlusButtons,
  onBrandingPositionChange,
  onPlusButtonClick,
  onReset,
  isResetEnabled,
  getBrandingPositionButtonVariant,
  getBrandingPositionButtonClassName,
  getPositionDisplayText,
}) => {
  const {t} = useTranslation();

  const renderBrandingRow = (type: 'header' | 'footer') => {
    const leftCenterKey = `${type}-left-center`;
    const centerRightKey = `${type}-center-right`;

    // Map type to proper BrandingPositionKey format
    const positionMap = {
      header: {
        left: 'leftHeaderBranding' as BrandingPositionKey,
        right: 'rightHeaderBranding' as BrandingPositionKey,
        center: 'centerHeaderBranding' as BrandingPositionKey,
        leftCenter: 'leftCenterHeaderBranding' as BrandingPositionKey,
        rightCenter: 'rightCenterHeaderBranding' as BrandingPositionKey,
        full: 'headerBranding' as BrandingPositionKey,
      },
      footer: {
        left: 'leftFooterBranding' as BrandingPositionKey,
        right: 'rightFooterBranding' as BrandingPositionKey,
        center: 'centerFooterBranding' as BrandingPositionKey,
        leftCenter: 'leftCenterFooterBranding' as BrandingPositionKey,
        rightCenter: 'rightCenterFooterBranding' as BrandingPositionKey,
        full: 'footerBranding' as BrandingPositionKey,
      },
    };

    const leftPosition = positionMap[type].left;
    const rightPosition = positionMap[type].right;
    const centerPosition = positionMap[type].center;
    const leftCenterPosition = positionMap[type].leftCenter;
    const rightCenterPosition = positionMap[type].rightCenter;
    const fullPosition = positionMap[type].full;

    return (
      <Box className="branding-row">
        {expandedPlusButtons[leftCenterKey] &&
        expandedPlusButtons[centerRightKey] ? (
          <ArrButton
            dataTestId={`${type}-full-expanded-btn`}
            variant={getBrandingPositionButtonVariant(fullPosition)}
            size="small"
            onClick={() => onBrandingPositionChange(fullPosition)}
            className={`${getBrandingPositionButtonClassName(fullPosition)} btn-full-expanded typo_text buttonSmall_size`}
          >
            Expanded
          </ArrButton>
        ) : (
          <>
            {/* Left Button or Left-Center Button */}
            {expandedPlusButtons[leftCenterKey] ? (
              <ArrButton
                dataTestId={`${type}-left-center-expanded-btn`}
                variant={getBrandingPositionButtonVariant(leftCenterPosition)}
                size="small"
                onClick={() => onBrandingPositionChange(leftCenterPosition)}
                className={`${getBrandingPositionButtonClassName(leftCenterPosition)} btn-half-expanded typo_text buttonSmall_size`}
              >
                Expanded
              </ArrButton>
            ) : (
              <ArrButton
                dataTestId={`${type}-left-btn`}
                variant={getBrandingPositionButtonVariant(leftPosition)}
                size="small"
                onClick={() => onBrandingPositionChange(leftPosition)}
                className={`${getBrandingPositionButtonClassName(leftPosition)} typo_text buttonSmall_size`}
              >
                Left
              </ArrButton>
            )}

            {/* Line - only show if left-center plus is not expanded */}
            {!expandedPlusButtons[leftCenterKey] && (
              <Box className="line"></Box>
            )}

            {/* Left-Center Plus Button */}
            {!expandedPlusButtons[leftCenterKey] && (
              <ArrButton
                variant="secondary"
                size="small"
                className="btn-expand"
                dataTestId={`${type}-left-center-plus-btn`}
                onClick={() => onPlusButtonClick(leftCenterKey)}
              >
                <AddIcon />
              </ArrButton>
            )}

            {/* Line - show if left-center is not expanded OR if center-right is expanded */}
            {(!expandedPlusButtons[leftCenterKey] ||
              expandedPlusButtons[centerRightKey]) && (
              <Box className="line"></Box>
            )}

            {/* Center Button - only show when no expansions are active */}
            {!expandedPlusButtons[leftCenterKey] &&
              !expandedPlusButtons[centerRightKey] && (
                <ArrButton
                  variant={getBrandingPositionButtonVariant(centerPosition)}
                  size="small"
                  onClick={() => onBrandingPositionChange(centerPosition)}
                  className={`${getBrandingPositionButtonClassName(centerPosition)} typo_text buttonSmall_size`}
                  dataTestId={`${type}-center-btn`}
                >
                  Center
                </ArrButton>
              )}

            {/* Right/Center Button - only show when center-right plus is expanded */}
            {expandedPlusButtons[centerRightKey] && (
              <ArrButton
                variant={getBrandingPositionButtonVariant(rightCenterPosition)}
                dataTestId={`${type}-right-center-expanded-btn`}
                size="small"
                onClick={() => onBrandingPositionChange(rightCenterPosition)}
                className={`${getBrandingPositionButtonClassName(rightCenterPosition)} btn-half-expanded typo_text buttonSmall_size`}
              >
                Expanded
              </ArrButton>
            )}

            {/* Line - only show if center-right plus is not expanded */}
            {!expandedPlusButtons[centerRightKey] && (
              <Box className="line"></Box>
            )}

            {/* Center-Right Plus Button */}
            {!expandedPlusButtons[centerRightKey] && (
              <ArrButton
                variant="secondary"
                dataTestId={`${type}-right-btn`}
                size="small"
                className="btn-expand"
                onClick={() => onPlusButtonClick(centerRightKey)}
              >
                <AddIcon />
              </ArrButton>
            )}

            {/* Line - only show if center-right plus is not expanded */}
            {!expandedPlusButtons[centerRightKey] && (
              <Box className="line"></Box>
            )}

            {/* Right Button */}
            {!expandedPlusButtons[centerRightKey] && (
              <ArrButton
                dataTestId="center-right"
                variant={getBrandingPositionButtonVariant(rightPosition)}
                size="small"
                onClick={() => onBrandingPositionChange(rightPosition)}
                className={`${getBrandingPositionButtonClassName(rightPosition)} typo_text buttonSmall_size`}
              >
                Right
              </ArrButton>
            )}
          </>
        )}
      </Box>
    );
  };

  return (
    <Box className="branding-location-section">
      <Typography
        className="typo-text subhead4_size branding-location-title"
        data-testid={`branding-location-title`}
      >
        {t(
          'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.branding.location.title'
        )}
      </Typography>
      <Box className="branding-location-grid">
        {/* Header Row */}
        {renderBrandingRow('header')}

        {/* Position Display */}
        <Box className="location-display">
          <Box className="location-box">
            <Typography
              className="typo-text label3_size location-label"
              data-testid={`branding-location-location-label`}
            >
              {t(
                'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.branding.location.locationLabel'
              )}
            </Typography>
            <Typography
              className="typo-text subhead1_size location-text"
              data-testid={`branding-location-position-text`}
            >
              {getPositionDisplayText(currentBrandingPosition)}
            </Typography>
            <ArrButton
              variant="secondary"
              size="small"
              className="reset-btn typo_text buttonSmall_size"
              onClick={onReset}
              disabled={!isResetEnabled}
              dataTestId="branding-location-reset-button"
            >
              {t(
                'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.branding.location.reset'
              )}
            </ArrButton>
          </Box>
        </Box>

        {/* Footer Row */}
        {renderBrandingRow('footer')}
      </Box>
    </Box>
  );
};

export default BrandingLocation;
